import { SolidButton } from 'components/Button';
import { H3, H4, P } from 'components/Text';
import Ticket from 'components/Ticket';
import { Amenity, FareTotalInPrefCurrency } from 'interfaces/flights';
import { Currencies } from 'interfaces/payment';
import { useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { getTotalFareInSelectedCurrency, renderPrice } from 'utils/utils';
import fromPairs from 'lodash/fromPairs';
import groupBy from 'lodash/groupBy';
import mapKeys from 'lodash/mapKeys';
import orderBy from 'lodash/orderBy';
import toPairs from 'lodash/toPairs';
import uniqBy from 'lodash/uniqBy';

interface ExtrasProps {
  totalPrice?: FareTotalInPrefCurrency[];
  currency: Currencies;
  handleSelect: (
    fare: FareTotalInPrefCurrency[],
    default_fare_family: string,
    packageBookingId?: string,
  ) => void;
  outboundCities: string;
  returnCities: string;
  outboundFareFamily: string;
  returnFareFamily?: string;
  outboundFareFamilyDesc?: Amenity[];
  returnFareFamilyDesc?: Amenity[];
  bookingId: string;
  showReturnFareFamily: boolean;
  width?: string;
  hidePrice?: boolean;
  hideSelect?: boolean;
  isRoundTrip?: boolean;
  numOfPassengers?: number;
  bgColor?: string;
}

// A group without a name is keyed by its type (CARRY_ON_BAGGAGE); spell it
// the way the API spells names (Carry On Baggage) so both share one key.
const categoryHeading = (groupType: string) =>
  /^[A-Z_]+$/.test(groupType)
    ? groupType
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : groupType;

// A machine-translated description (APY-1454) is marked as such, and the
// airline's English original can be revealed (WHIT-417).
const FareDescription: React.FC<{ item: Amenity }> = ({ item }) => {
  const { t } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  const isMachine =
    item.description_translation === 'machine' && !!item.description_original;

  return (
    <div className="mb-1">
      <P className="break-words">{item.description}</P>
      {isMachine && (
        <>
          <p className="text-xs text-gray-500">
            {t('Automatically translated')}
            {' · '}
            <button
              type="button"
              className="underline"
              aria-expanded={showOriginal}
              onClick={() => setShowOriginal(open => !open)}>
              {showOriginal ? t('Hide original') : t('Show original')}
            </button>
          </p>
          {showOriginal && (
            <p lang="en" className="text-xs text-gray-500 italic break-words">
              {item.description_original}
            </p>
          )}
        </>
      )}
    </div>
  );
};

const Extras: React.FC<ExtrasProps> = props => {
  const {
    outboundCities,
    returnCities,
    outboundFareFamily,
    returnFareFamily,
    outboundFareFamilyDesc,
    returnFareFamilyDesc,
    totalPrice,
    currency,
    handleSelect,
    showReturnFareFamily,
    bookingId,
    numOfPassengers,
    isRoundTrip,
    width = 'w-[90%]',
    hidePrice = false,
    hideSelect = false,
    bgColor = 'bg-tt-white-purple',
  } = props;
  const { t } = useTranslation();

  const renderFareFamilyDescriptionItems = (fareFamilyDesc?: Amenity[]) => {
    // Filter out non chargeable items
    const finalFarefamilyDesc = fareFamilyDesc?.filter(
      el => el && !el.isChargeable && el.description,
    );

    // Filter out objects with unique descriptions
    const uniqueDescriptions = uniqBy(finalFarefamilyDesc, 'description');

    // Group remaining objects by type
    const groupedItems = groupBy(uniqueDescriptions, item => item.type);

    // Change the key to the "name" field exists otherwise fall back to the "type" field
    const updatedGroupedItems = mapKeys(groupedItems, (value, key) => {
      const itemWithKey = value.find(item => item.name);
      return itemWithKey ? itemWithKey.name : key;
    });

    // Sort the updatedGroupedItems by key (group name)
    const sortedGroupedItems = fromPairs(
      orderBy(toPairs(updatedGroupedItems), 0),
    );

    return (
      <div className="mt-4 flex flex-col">
        {Object.entries(sortedGroupedItems).map(([groupType, group], idx) => (
          <div className="mb-3" key={idx}>
            <P>
              <b>{t(categoryHeading(groupType))}</b>
            </P>
            <div>
              {/* Descriptions are shown as the API sends them; the API
                  localizes them when the request asks for a language. */}
              {group.map((item, itemIdx) => (
                <FareDescription
                  key={`${groupType}_${item.description}_${itemIdx}`}
                  item={item}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`flex flex-col ${width} ${bgColor} rounded-xl p-4 flex-none`}>
      <div className="flex space-x-6 flex-1">
        <div
          className={`flex-1 ${showReturnFareFamily && 'border-r-[1px] pr-2'}`}>
          <H4>{outboundCities}</H4>
          <div>
            <H4 className="text-tt-verydark-purple tt-gopher w-full !text-[1rem] mt-2">
              {outboundFareFamily}
            </H4>
            {renderFareFamilyDescriptionItems(outboundFareFamilyDesc)}
          </div>
        </div>
        {showReturnFareFamily && (
          <div className="flex-1">
            <H4>{returnCities}</H4>
            <div>
              <H4 className="text-tt-verydark-purple tt-gopher w-full !text-[1rem] mt-2">
                {returnFareFamily}
              </H4>
              {renderFareFamilyDescriptionItems(returnFareFamilyDesc)}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col mt-auto justify-center">
        {!hidePrice && (
          <H3
            className={`text-center text-tt-dark-purple dm-sans font-bold ${
              numOfPassengers ? 'mt-6' : 'my-6'
            }`}>
            {renderPrice(
              currency,
              getTotalFareInSelectedCurrency(currency, totalPrice),
            )}
          </H3>
        )}
        {numOfPassengers && (
          <Ticket
            className="justify-center"
            isRoundTrip={isRoundTrip || false}
            numOfPassengers={numOfPassengers}
          />
        )}
        {!hideSelect && (
          <SolidButton
            className={`flex justify-center self-center w-1/2 md:w-1/3 text-lg ${
              numOfPassengers ? 'my-6' : 'mt-auto'
            }`}
            // The card's heading names the package, so the button does not
            // repeat it (WHIT-506); a screen reader still hears which one,
            // as three "Choose" buttons side by side do not say (WHIT-494).
            aria-label={t('Choose package', { fare: outboundFareFamily })}
            onClick={() =>
              handleSelect(totalPrice || [], outboundFareFamily, bookingId)
            }>
            {t('Choose')}
          </SolidButton>
        )}
      </div>
    </div>
  );
};
export default Extras;
